<template>
	<HomeItemLayout class="layout">
		<DoughnutChartItem
			v-if="isFetched"
			:data="chartOutlineData"
			:date="getMajorHolderEventData?.major_holder_event[0].major_holder_base_date"
		/>
	</HomeItemLayout>
	<ListLayout v-if="getMajorHolderEventData" class="layout">
		<ExpandableTitleBar
			title="투자 비중 Top10"
			:has-view="false"
			:time="`${getMajorHolderEventData.major_holder_event[0].major_holder_base_date} 기준`"
		/>
		<BookmarkTable
			is-no
			is-percent
			:data="getMajorHolderEventData.major_holder_proportion"
			@update:book-mark-refetch="updateBookmark"
		/>
	</ListLayout>
	<ListLayout
		v-if="getMajorHolderEventData && getMajorHolderEventData.major_holder_event[0].major_holder_newly_date"
		class="layout"
	>
		<ExpandableTitleBar
			title="신규 편입 종목"
			:has-view="false"
			:time="`${getMajorHolderEventData.major_holder_event[0].major_holder_newly_date} 기준`"
		/>
		<BookmarkTable
			:data="getMajorHolderEventData.major_holder_newly_added"
			@update:book-mark-refetch="updateBookmark"
		/>
	</ListLayout>
	<ListLayout
		v-if="getMajorHolderEventData && getMajorHolderEventData.major_holder_event[0].major_holder_selling_date"
		class="layout"
	>
		<ExpandableTitleBar
			title="매도 종목"
			:has-view="false"
			:time="`${getMajorHolderEventData.major_holder_event[0].major_holder_selling_date} 기준`"
		/>
		<BookmarkTable
			is-percent
			:data="getMajorHolderEventData.major_holder_selling"
			@update:book-mark-refetch="updateBookmark"
		/>
	</ListLayout>
	<ListLayout class="layout">
		<p class="description-caution">
			※ 큰손들의 실제 보유종목에 표시되는 종목은 대가의 선택에서 기업 분석이 제공되는 종목에 한정되며, 이로 인해 실제
			보유 내역과 차이가 있을 수 있음을 참고 바랍니다.
		</p>
	</ListLayout>
</template>

<script setup lang="ts">
// core
import { computed } from 'vue';
import { useRoute } from 'vue-router';

// api
import { useGetMajorHolderEventApi } from '@/api/majorHolder';
import DoughnutChartItem from '@/components/atom/chart/DoughnutChartItem.vue';
import ExpandableTitleBar from '@/components/atom/tabBar/ExpandableTitleBar.vue';
// component
import BookmarkTable from '@/components/atom/table/BookmarkTable.vue';
import HomeItemLayout from '@/components/home/HomeItemLayout.vue';
import ListLayout from '@/components/layout/main/ListLayout.vue';

// -----variable-----
const props = defineProps<{
	pk: number;
}>();

const route = useRoute();
const pk = computed(() => route.query.pk ?? props.pk);
const params = computed(() => ({ major_holder_event_pk: String(pk.value) }));

// -----api-----
const {
	data: getMajorHolderEventData,
	refetch: refetchGetMajorHolderEvent,
	isFetched,
} = useGetMajorHolderEventApi(params, { enabled: computed(() => !!pk.value) });

// -----chart-----
const chartOutlineData = computed(() => ({
	labels:
		getMajorHolderEventData.value?.major_holder_proportion.map((item) => String(item.major_holder_proportion_name)) ??
		[],
	datasets: [
		{
			data:
				getMajorHolderEventData.value?.major_holder_proportion.map((item) => Number(item.major_holder_proportion)) ??
				[],
			backgroundColor: [
				'#01A9FF',
				'#FFB83F',
				'#FF5946',
				'#DBAEFF',
				'#A9F4D0',
				'#2B8B5D',
				'#F49698',
				'#FEFFD5',
				'#CFD096',
				'#EAEAEA',
				'#3E61FF',
			],
			borderColor: 'transparent',
		},
	],
}));

// -----function-----
const updateBookmark = async () => {
	await refetchGetMajorHolderEvent();
};
</script>

<style lang="scss" scoped>
.layout {
	@media screen and (max-width: 1280px) {
		padding-left: 2rem;
		padding-right: 2rem;
	}
}
.description-caution {
	color: var(--color-base-light);
	font-size: var(--font-s-mid);
	font-weight: var(--font-w-light);
	line-height: 1.9rem;
}
</style>
